import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CommonService } from '../common.service';
import { ChatMessage, FileAttachment } from '../nova-service.service';
import { environment } from '../../../environments/environment';

export interface NovaApiRequest {
  message: string;
  attachments?: FileAttachment[];
  conversationId?: string;
}

export interface NovaApiResponse {
  response: string;
  conversationId: string;
  timestamp: Date;
}

export interface ConversationItem {
  conversation_id: string;
  timestamp: string; // initial creation time
  last_updated: string; // last updated time from API
  conversation_title: string;
}

export interface ConversationsData {
  conversations: ConversationItem[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  period: string;
}

export interface GetConversationsResponse {
  id: string;
  object: string;
  created: number;
  data: ConversationsData;
  success: boolean;
  message: string;
}

export interface GetConversationsRequest {
  period: string;
  page: number;
  page_size: number;
}

// Chat API interfaces
/** Payload format for chat: query + custom_filters (date range, timezone, filters). */
export interface NovaChatCustomFilters {
  start_date: string;
  end_date: string;
  timeZone: string;
  filters: Array<{ logicalOperator?: string; columnName: string; operationType: string; value: string | string[]; isClusterFilter?: boolean; clusterFilters?: unknown[] }>;
}

export interface NovaChatPayload {
  query: string;
  custom_filters: NovaChatCustomFilters;
}

/** Chat request body: always sent with nested custom_filters. */
export interface ChatRequest {
  query: string;
  conversation_id?: string;
  custom_filters: NovaChatCustomFilters;
}

export interface ChatResponse {
  id: string;
  object: string;
  created: number;
  data: {
    job_id: string;
    message_id: string;
    conversation_id: string;
    status: string;
    submitted_at: string;
    query: string;
  };
  success: boolean;
  message: string;
}

export interface ChatStatusRequest {
  job_id: string;
  conversation_id: string;
}

/** Metadata file item from API (files array in response.metadata) */
export interface MetadataFileItem {
  file_id: string;
  file_url: string;
  file_name?: string;
  file_type: string;
  file_format?: string;
  file_title?: string;
  file_description?: string;
  step?: string;
  start_date?: string;
  end_date?: string;
  url_expired_at?: string;
}

/** Metadata chart item from API (charts array in response.metadata) */
export interface MetadataChartItem {
  file_id: string;
  file_url: string;
  thumbnail_url?: string;
  chart_title?: string;
  chart_description?: string;
  file_type?: string;
  step?: string;
  start_date?: string;
  end_date?: string;
  url_expired_at?: string;
  thumbnail_url_expired_at?: string;
}

/** Metadata table item from API (tables array in response.metadata) */
export interface MetadataTableItem {
  step?: string;
  table_title?: string;
  table_description?: string;
  table_content: string; // markdown table
  start_date?: string | null;
  end_date?: string | null;
}

export interface ResponseMetadata {
  files?: MetadataFileItem[];
  tables?: MetadataTableItem[];
  charts?: MetadataChartItem[];
  filters_applied?: { filter_name: string; filter_values: string[] }[];
}

/** Filter applied - for UI display */
export interface FilterApplied {
  filter_name: string;
  filter_values: string[];
}

export interface SqlQueryApplied {
  step: number;
  sql_query: string;
}

/** API filter item from async-chat-status / conversation-messages (conversation-specific context). */
export interface ConversationContextFilter {
  columnName: string;
  operationType: string;
  value: string | string[];
}

export interface ChatStatusResponse {
  id: string;
  object: string;
  created: number;
  data: {
    job_id: string;
    conversation_id: string;
    message_id: string;
    status: string;
    timestamp: string;
    query: string;
    conversation_title: string;
    batch_id?: string | null;
    /** Human-readable description of the current processing step (shown in typing indicator). */
    current_step?: string;
    progress_percentage?: number;
    /** Conversation-specific context (preferred when present). */
    active_custom_filters?: ActiveCustomFilters;
    /** Legacy flat context when API does not use active_custom_filters. */
    filters?: ConversationContextFilter[];
    start_date?: string | null;
    end_date?: string | null;
    timeZone?: string | null;
    response: {
      /** Persisted message id (sc_...) — the context_id expected by download-section-data. */
      context_id?: string;
      user_message: string;
      system_message: string;
      sql_queries_applied?: SqlQueryApplied[];
      metadata?: ResponseMetadata;
    };
  };
  success: boolean;
  message: string;
}

export interface ConversationMessagesRequest {
  conversation_id: string;
}

export interface ConversationMessage {
  context_id: string;
  user_message: string;
  system_message: string;
  timestamp: string;
  metadata?: ResponseMetadata;
  sql_queries_applied?: SqlQueryApplied[];
  agent_outputs?: any;
}

/** Conversation-specific filters, daterange, timezone (from API active_custom_filters). */
export interface ActiveCustomFilters {
  filters?: ConversationContextFilter[];
  start_date?: string | null;
  end_date?: string | null;
  timeZone?: string | null;
}

export interface ConversationMessagesResponse {
  id: string;
  object: string;
  created: number;
  data: {
    conversation_metadata: {
      conversation_id: string;
      user_id: string;
      application_id: string;
      timestamp: string;
      last_updated?: string;
      conversation_title: string;
      batch_id?: string | null;
      /** Conversation-specific context (filters, daterange, timezone) – preferred source. */
      active_custom_filters?: ActiveCustomFilters;
      /** Legacy/flat context when API does not use active_custom_filters. */
      filters?: ConversationContextFilter[];
      start_date?: string | null;
      end_date?: string | null;
      timeZone?: string | null;
    };
    messages: ConversationMessage[];
    total_messages: number;
  };
  success: boolean;
  message: string;
}

// Get conversation custom filters (date range, timezone, filters) when conversation changes
export interface GetConversationCustomFiltersRequest {
  conversation_id: string;
}

export interface GetConversationCustomFiltersResponse {
  id: string;
  object: string;
  created: number;
  data: {
    conversation_id: string;
    active_custom_filters: ActiveCustomFilters;
  };
  success: boolean;
  message: string;
}

// Delete conversation interfaces
export interface DeleteConversationRequest {
  conversation_id: string;
}

export interface DeleteConversationResponse {
  id: string;
  object: string;
  created: number;
  data: {
    conversation_id: string;
    deleted_at: string;
  };
  success: boolean;
  message: string;
}

// Rename conversation interfaces
export interface RenameConversationRequest {
  conversation_id: string;
  new_conversation_title: string;
}

export interface RenameConversationResponse {
  id: string;
  object: string;
  created: number;
  data: {
    conversation_id: string;
    conversation_title: string;
    updated_at: string;
  };
  success: boolean;
  message: string;
}

// Save file interfaces
export interface SaveFileRequest {
  file_id: string;
  file_type: 'chart' | 'file';
}

export interface SaveFileResponse {
  id: string;
  object: string;
  created: number;
  data: {
    saved_file_id: string;
    file_type: string;
    saved_timestamp: string;
    message: string;
  };
  success: boolean;
  message: string;
}

// Get saved files interfaces
export interface GetSavedFilesRequest {
  period: string;
  page: number;
  page_size: number;
}

export interface SavedFile {
  saved_file_id: string;
  file_type: 'chart' | 'file';
  generated_timestamp: string;
  saved_timestamp: string;
  file_url: string;
  thumbnail_url?: string;
  saved_file_title?: string;
  saved_file_start_date?: string;
  saved_file_end_date?: string;
}

export interface SavedFilesData {
  files: SavedFile[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  period: string;
  file_type: string | null;
}

export interface GetSavedFilesResponse {
  id: string;
  object: string;
  created: number;
  data: SavedFilesData;
  success: boolean;
  message: string;
}

// Delete saved file interfaces
export interface DeleteSavedFileRequest {
  saved_file_id: string;
}

export interface DeleteSavedFileResponse {
  id: string;
  object: string;
  created: number;
  data: {
    saved_file_id: string;
    message: string;
  };
  success: boolean;
  message: string;
}

// Get sample questions interfaces
export interface GetSampleQuestionsRequest {
  application_id: string;
}

export interface GetSampleQuestionsResponse {
  id: string;
  object: string;
  created: number;
  data: {
    application_id: string;
    sample_questions: string[];
  };
  success: boolean;
  message: string;
}

// Download section data (CSV) interfaces
export interface DownloadSectionDataRequest {
  conversation_id: string;
  context_id: string;
  step: string | number;
  application_id?: string;
}

export interface SectionDataUrlResponse {
  id: string;
  object: string; // "SectionDataUrl" | "ErrorResponse"
  created: number;
  /** ALWAYS branch on this — errors are returned as HTTP 200 with success:false */
  success: boolean;
  message: string;
  data: { url: string; expires_at: string } | null;
}

@Injectable({
  providedIn: 'root'
})
export class NovaApiService {
  private baseUrl = environment.NOVA_BASE_URL;
  public commonService = inject(CommonService);

  constructor(private http: HttpClient) { }

  /**
   * Send a message to Nova API
   */
  sendMessage(request: NovaApiRequest): Observable<NovaApiResponse> {
    return this.http.post<NovaApiResponse>(`${this.baseUrl}/message`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Upload file attachment
   */
  uploadFile(file: File): Observable<FileAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<FileAttachment>(`${this.baseUrl}/upload`, formData, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Get conversation history
   */
  getConversationHistory(conversationId: string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(`${this.baseUrl}/conversation/${conversationId}`, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Create a new conversation
   */
  createConversation(): Observable<{ conversationId: string }> {
    return this.http.post<{ conversationId: string }>(`${this.baseUrl}/conversation`, {}, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Delete a conversation
   */
  deleteConversation(conversationId: string): Observable<DeleteConversationResponse> {
    const request: DeleteConversationRequest = {
      conversation_id: conversationId
    };
    return this.http.post<DeleteConversationResponse>(`${this.baseUrl}/navigator/delete-conversation`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Rename conversation
   */
  renameConversation(conversationId: string, newTitle: string): Observable<RenameConversationResponse> {
    const request: RenameConversationRequest = {
      conversation_id: conversationId,
      new_conversation_title: newTitle
    };
    return this.http.post<RenameConversationResponse>(`${this.baseUrl}/navigator/rename-conversation`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Get all conversations from Nova API
   */
  getConversations(request: GetConversationsRequest = {
    period: 'all',
    page: 1,
    page_size: 1000
  }): Observable<GetConversationsResponse> {
    return this.http.post<GetConversationsResponse>(`${this.baseUrl}/navigator/conversations`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Submit a chat query to Nova API
   */
  submitChat(request: ChatRequest): Observable<ChatResponse> {
    return this.http.post<ChatResponse>(`${this.baseUrl}/navigator/chat`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Generate a presigned CSV URL for a chart/table section's underlying data.
   * Errors come back as HTTP 200 with success:false — branch on the success field.
   */
  downloadSectionData(request: DownloadSectionDataRequest): Observable<SectionDataUrlResponse> {
    return this.http.post<SectionDataUrlResponse>(`${this.baseUrl}/navigator/download-section-data`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Check async chat status
   */
  checkChatStatus(request: ChatStatusRequest): Observable<ChatStatusResponse> {
    return this.http.post<ChatStatusResponse>(`${this.baseUrl}/navigator/async-chat-status`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Get conversation messages
   */
  getConversationMessages(request: ConversationMessagesRequest): Observable<ConversationMessagesResponse> {
    return this.http.post<ConversationMessagesResponse>(`${this.baseUrl}/navigator/conversation-messages`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Get conversation-specific custom filters (date range, timezone, filters). Call when conversation ID changes.
   */
  getConversationCustomFilters(request: GetConversationCustomFiltersRequest): Observable<GetConversationCustomFiltersResponse> {
    return this.http.post<GetConversationCustomFiltersResponse>(`${this.baseUrl}/navigator/conversation-active-custom-filters`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Save file/chart to user's saved files
   */
  saveFile(request: SaveFileRequest): Observable<SaveFileResponse> {
    return this.http.post<SaveFileResponse>(`${this.baseUrl}/navigator/save-file`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Get all saved files
   */
  getSavedFiles(request: GetSavedFilesRequest = {
    period: 'all',
    page: 1,
    page_size: 1000
  }): Observable<GetSavedFilesResponse> {
    return this.http.post<GetSavedFilesResponse>(`${this.baseUrl}/navigator/saved-files`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Delete a saved file
   */
  deleteSavedFile(request: DeleteSavedFileRequest): Observable<DeleteSavedFileResponse> {
    return this.http.post<DeleteSavedFileResponse>(`${this.baseUrl}/navigator/delete-saved-file`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }

  /**
   * Get sample questions
   */
  getSampleQuestions(request: GetSampleQuestionsRequest): Observable<GetSampleQuestionsResponse> {
    return this.http.post<GetSampleQuestionsResponse>(`${this.baseUrl}/navigator/get-sample-questions`, request, {
      headers: this.commonService.getAuthHeaders()
    });
  }
}
